/**
 * useAIGeneration Hook
 *
 * Provides loading states and progress tracking for AI generation.
 */

import { useState, useCallback, useRef } from 'react';

/**
 * Generation stage
 */
export type GenerationStage =
  | 'idle'
  | 'preparing'
  | 'generating'
  | 'processing'
  | 'compiling'
  | 'complete'
  | 'error';

/**
 * Generation progress info
 */
export interface GenerationProgress {
  stage: GenerationStage;
  message: string;
  progress: number; // 0-100
}

/**
 * Stage messages
 */
const STAGE_MESSAGES: Record<GenerationStage, string> = {
  idle: 'Ready to generate',
  preparing: 'Preparing your request...',
  generating: 'AI is creating your presentation...',
  processing: 'Processing content blocks...',
  compiling: 'Compiling to FlowVella format...',
  complete: 'Presentation ready!',
  error: 'An error occurred',
};

/**
 * Stage progress values
 */
const STAGE_PROGRESS: Record<GenerationStage, number> = {
  idle: 0,
  preparing: 10,
  generating: 30,
  processing: 60,
  compiling: 85,
  complete: 100,
  error: 0,
};

/**
 * useAIGeneration return type
 */
export interface UseAIGenerationReturn {
  stage: GenerationStage;
  message: string;
  progress: number;
  isGenerating: boolean;
  startGeneration: () => void;
  setStage: (stage: GenerationStage) => void;
  complete: () => void;
  fail: (errorMessage?: string) => void;
  reset: () => void;
}

/**
 * useAIGeneration hook
 */
export function useAIGeneration(): UseAIGenerationReturn {
  const [stage, setStageState] = useState<GenerationStage>('idle');
  const [customMessage, setCustomMessage] = useState<string | null>(null);
  const progressInterval = useRef<NodeJS.Timeout | null>(null);

  /**
   * Set the current stage
   */
  const setStage = useCallback((newStage: GenerationStage) => {
    setStageState(newStage);
    setCustomMessage(null);

    // Clear any existing progress animation
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
  }, []);

  /**
   * Start the generation process
   */
  const startGeneration = useCallback(() => {
    setStage('preparing');

    // Animate through stages
    setTimeout(() => setStage('generating'), 500);
  }, [setStage]);

  /**
   * Mark generation as complete
   */
  const complete = useCallback(() => {
    setStage('complete');
  }, [setStage]);

  /**
   * Mark generation as failed
   */
  const fail = useCallback((errorMessage?: string) => {
    setStage('error');
    if (errorMessage) {
      setCustomMessage(errorMessage);
    }
  }, [setStage]);

  /**
   * Reset to idle state
   */
  const reset = useCallback(() => {
    setStage('idle');
    setCustomMessage(null);
  }, [setStage]);

  const message = customMessage || STAGE_MESSAGES[stage];
  const progress = STAGE_PROGRESS[stage];
  const isGenerating = stage !== 'idle' && stage !== 'complete' && stage !== 'error';

  return {
    stage,
    message,
    progress,
    isGenerating,
    startGeneration,
    setStage,
    complete,
    fail,
    reset,
  };
}
